import type { CustomOptionGroup, CustomOptionPreset, CustomOptionValue } from "../../api/products";

export type ParameterKind = "text" | "image" | "quantity" | "size" | "number-input" | "text-input";
export interface MaterialAsset { id: string; name: string; imageUrl: string }
export function validateLocalImage(file: Pick<File, "type" | "size">): void {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("请选择 JPG、PNG 或 WebP 图片");
  if (file.size <= 0 || file.size > 5 * 1024 * 1024) throw new Error("图片不能为空，且不能超过 5MB");
}
export interface ParameterTemplate { name: string; codes: string[] }

// Public library snapshots are copied, never linked or changed in place.
export function groupsFromPresets(presets: readonly CustomOptionPreset[], productId: string): CustomOptionGroup[] {
  return presets.map(preset => {
    const id = `draft:${crypto.randomUUID()}`;
    return { ...JSON.parse(JSON.stringify(preset)), id, productId,
      values: preset.values.map(value => ({
        id: `draft:${crypto.randomUUID()}`, groupId: id, code: value.code, name: value.name,
        imageUrl: value.imageUrl, sortOrder: value.sortOrder, isActive: value.isActive,
        createdAt: value.createdAt, updatedAt: value.updatedAt, extraConfig: null })) };
  });
}
// Optional bridge only: no speculative keys are written into existing JSON.
export interface ParameterBridge {
  readPrice?: (value: CustomOptionValue) => number;
  writePrice?: (value: CustomOptionValue, cents: number) => CustomOptionValue;
  readCustom?: (group: CustomOptionGroup) => boolean;
  writeCustom?: (group: CustomOptionGroup, enabled: boolean) => CustomOptionGroup;
}

export function cloneGroups(groups: readonly CustomOptionGroup[]): CustomOptionGroup[] {
  return JSON.parse(JSON.stringify(groups)) as CustomOptionGroup[];
}

export function uniqueCode(): string {
  // Fits the existing API's uppercase, <=64 character code validation.
  return `OPT_${crypto.randomUUID().replaceAll("-", "").toUpperCase()}`;
}

export function parameterKind(group: CustomOptionGroup): ParameterKind {
  if (/^(QUANTITY|EST_QTY)$/.test(group.code) || /^(数量|订购数量|预计数量)$/.test(group.name)) return "quantity";
  if (group.code === "FINISHED_SIZE" || /尺寸/.test(group.name)) return "size";
  if (group.inputType === "NUMBER") return "number-input";
  if (group.inputType === "TEXT") return "text-input";
  if (/^(MATERIAL|PRINT|PAPER_WEIGHT)$/.test(group.code) || /^(材质|印面|克重)$/.test(group.name)) return "text";
  if (/覆膜|工艺|流沙边|配件/.test(group.name) || group.values.some(value => value.imageUrl)) return "image";
  return "text";
}

export function activeValues(group: CustomOptionGroup): CustomOptionValue[] {
  return group.values.filter(value => value.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function addNames(group: CustomOptionGroup, names: string[], assets: MaterialAsset[] = []): CustomOptionGroup {
  const next = cloneGroups([group])[0];
  for (const [index, raw] of names.entries()) {
    const name = raw.trim();
    if (!name || name.length > 100) throw new Error("选项名称需为 1–100 个字符");
    const asset = assets[index];
    if (next.values.some(value => value.isActive && (asset ? value.imageUrl === asset.imageUrl : value.name === name))) continue;
    // Restore a matching inactive record rather than duplicating its legacy identity.
    const old = next.values.find(value => !value.isActive && value.name === name && (!asset || value.imageUrl === asset.imageUrl));
    if (old) {
      old.isActive = true;
      old.sortOrder = Math.max(-1, ...next.values.map(value => value.sortOrder)) + 1;
      continue;
    }
    const now = new Date().toISOString();
    next.values.push({ id: `draft:${crypto.randomUUID()}`, groupId: group.id, code: uniqueCode(), name,
      extraConfig: null, imageUrl: asset?.imageUrl ?? null,
      sortOrder: Math.max(-1, ...next.values.map(value => value.sortOrder)) + 1,
      isActive: true, createdAt: now, updatedAt: now });
  }
  return next;
}

export function quantityNames(text: string): string[] {
  const names = text.split(/[,，、\s]+/).filter(Boolean);
  if (!names.length || names.length > 100) throw new Error("请输入 1–100 个数量阶梯，以逗号分隔");
  for (const name of names) {
    if (!/^[1-9]\d*$/.test(name) || !Number.isSafeInteger(Number(name))) throw new Error("数量需为正整数，例如 50, 100, 500");
  }
  return [...new Set(names)];
}

export function priceCents(text: string): number {
  if (!/^\d+(\.\d{1,2})?$/.test(text)) throw new Error("加价需为非负金额，最多两位小数");
  const [whole, fraction = ""] = text.split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents)) throw new Error("加价金额过大");
  return cents;
}

// Called by drag onEnd. Unknown JSON and all existing identities remain intact.
export function reorderValues(group: CustomOptionGroup, from: string, to: string): CustomOptionGroup {
  const next = cloneGroups([group])[0];
  const visible = activeValues(next);
  const source = visible.findIndex(value => value.id === from);
  const target = visible.findIndex(value => value.id === to);
  if (source < 0 || target < 0 || source === target) return next;
  visible.splice(target, 0, visible.splice(source, 1)[0]);
  visible.forEach((value, index) => { value.sortOrder = index; });
  return next;
}

export function removeValue(group: CustomOptionGroup, id: string): CustomOptionGroup {
  const next = cloneGroups([group])[0];
  // Never physically delete an existing option referenced by historical inquiries.
  next.values = next.values.filter(value => !(value.id === id && value.id.startsWith("draft:")));
  const value = next.values.find(item => item.id === id);
  if (value) value.isActive = false;
  return next;
}

// A reviewable write plan, NOT an API batch and NOT an automatic save/retry loop.
// Each update retains the original CAS timestamp. The host must handle partial success.
export interface ValueChange {
  kind: "create" | "update";
  groupId: string;
  valueId: string;
  body: { expectedUpdatedAt?: string; code: string; name: string; imageUrl: string | null; extraConfig: unknown; sortOrder: number };
  status?: "ACTIVE" | "INACTIVE";
}
export function buildValueChanges(before: CustomOptionGroup[], after: CustomOptionGroup[]): ValueChange[] {
  return after.flatMap(group => group.values.flatMap<ValueChange>(value => {
    const original = before.find(item => item.id === group.id)?.values.find(item => item.id === value.id);
    if (!original) return [{ kind: "create" as const, groupId: group.id, valueId: value.id,
      body: { code: value.code, name: value.name, imageUrl: value.imageUrl,
        extraConfig: value.extraConfig, sortOrder: value.sortOrder } }];
    if (JSON.stringify(value) === JSON.stringify(original)) return [];
    return [{ kind: "update" as const, groupId: group.id, valueId: value.id,
      body: { expectedUpdatedAt: original.updatedAt, code: value.code, name: value.name,
        imageUrl: value.imageUrl, extraConfig: value.extraConfig, sortOrder: value.sortOrder },
      status: value.isActive === original.isActive ? undefined : value.isActive ? "ACTIVE" as const : "INACTIVE" as const }];
  }));
}

export function buildGroupOrderChanges(before: CustomOptionGroup[], after: CustomOptionGroup[]) {
  return after.flatMap(group => {
    const original = before.find(item => item.id === group.id);
    return original && original.sortOrder !== group.sortOrder
      ? [{ groupId: group.id, body: { expectedUpdatedAt: original.updatedAt, sortOrder: group.sortOrder } }]
      : [];
  });
}
